import { useEffect, useState } from 'react';
import { usePresence } from '@accounts/session';
import { Button, Consequence, Dialog } from '@beyond-js/ui/react';
import { ActingChoice } from '@accounts/ui';

/** Which sessions a confirmation ends: every other one, or every one, this browser's included */
export type Reach = 'others' | 'everywhere';

/**
 * The confirmation of signing out of other sessions or everywhere, with the choice to end what acts for
 * the person beyond their sessions, their AI apps and access tokens, unticked (D196). Signing out of
 * everywhere states what is lost and kept as the choice stands. The shared `Dialog` holds it because the
 * shared confirmation takes no choice.
 */
export function Ending({ reach, onCancel, onAccept }: { reach?: Reach; onCancel: () => void; onAccept: (access: boolean) => void }) {
	const { text, language } = usePresence();
	const [acting, setActing] = useState(false);
	// Asked afresh each time the confirmation opens
	useEffect(() => {
		if (reach) setActing(false);
	}, [reach]);

	const everywhere = reach === 'everywhere';
	return (
		<Dialog
			open={Boolean(reach)}
			size="small"
			title={text(everywhere ? 'sessions.everywhere.title' : 'devices.others.title')}
			description={text(everywhere ? 'sessions.everywhere.body' : 'devices.others.body')}
			labels={Dialog.labels[language]}
			onClose={onCancel}
			actions={
				<>
					<Button variant="quiet" onClick={onCancel}>
						{text('action.cancel')}
					</Button>
					<Button variant="danger" onClick={() => onAccept(acting)}>
						{text(everywhere ? 'action.signout.everywhere' : 'devices.others')}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-3">
				{everywhere ? (
					<Consequence
						parts={
							acting
								? { lost: text('sessions.everywhere.lost.access'), kept: text('sessions.everywhere.kept.access'), recovery: text('sessions.everywhere.recovery.access') }
								: { lost: text('sessions.everywhere.lost'), kept: text('sessions.everywhere.kept'), recovery: text('sessions.everywhere.recovery') }
						}
						labels={Consequence.labels[language]}
					/>
				) : null}
				<ActingChoice checked={acting} onChange={setActing} />
			</div>
		</Dialog>
	);
}
